import { useCallback } from "react";

import { VisibilityState } from "@/components/ColumnVisibilityToggle";

/**
 * React hook to manage the visibility state of a set of columns.
 * @param columnSetIds Set of column IDs to manage as a group
 * @param columnVisibility Current column visibility state
 * @param setColumnVisibility Setter for column visibility state
 * @returns A tuple containing the visibility state of the column set and a function to toggle its visibility.
 */
export function useColumnSetVisibility(
  columnSetIds: Set<string>,
  columnVisibility: VisibilityState,
  setColumnVisibility: (updater: (prev: VisibilityState) => VisibilityState) => void,
): [boolean | undefined, () => void] {
  const isVisible = getVisibilityStateForColumnSet(columnSetIds, columnVisibility);

  const toggleVisibility = useCallback(() => {
    setColumnVisibility((prev) => updateColumnSetVisibility(columnSetIds, !isVisible, prev));
  }, [isVisible, setColumnVisibility, columnSetIds]);

  return [isVisible, toggleVisibility];
}

/**
 * Helper function to update the visibility of a set of columns.
 *
 * @param columnIds The set of column IDs to update.
 * @param newVisibility The new visibility state for the columns.
 * @param visibilityState The current visibility state of the columns.
 * @returns A new visibility state object with the updated visibility.
 */
function updateColumnSetVisibility(
  columnIds: Set<string>,
  newVisibility: boolean,
  visibilityState: VisibilityState,
): VisibilityState {
  return newVisibility ?
      // Return a new object with the specified columns all removed from the visibility state
      Object.fromEntries(Object.entries(visibilityState).filter(([colId]) => !columnIds.has(colId)))
      // Return a new object with the specified columns all set to false
    : {
        ...visibilityState,
        ...Object.fromEntries([...columnIds].map((colId) => [colId, false])),
      };
}

/**
 * Get the visibility state for a set of columns. If all columns have the same visibility value, return that value. If
 * columns have mixed visibility, return undefined (indeterminate).
 */
function getVisibilityStateForColumnSet(columnIds: Set<string>, visibilityState: VisibilityState): boolean | undefined {
  const uniqueColumnVisibilities = new Set(
    [...columnIds].map((colId) => (visibilityState[colId] !== false ? true : false)),
  );
  return uniqueColumnVisibilities.size === 1 ? uniqueColumnVisibilities.values().next().value : undefined;
}
